if (!customElements.get('featured-video-shopping')) {
  customElements.define(
    'featured-video-shopping',
    class FeaturedVideoShopping extends HTMLElement {
      connectedCallback() {
        if (this.dataset.featuredVideoReady === 'true') return;

        this.dataset.featuredVideoReady = 'true';
        this.rail = this.querySelector('[data-featured-video-rail]');
        this.railControls = this.querySelector('[data-featured-video-rail-controls]');
        this.dialog = this.querySelector('[data-featured-video-dialog]');
        this.viewer = this.querySelector('[data-featured-video-viewer]');
        this.templates = Array.from(this.querySelectorAll('[data-featured-video-template]'));
        this.modalAutoplay = this.dataset.modalAutoplay === 'true';
        this.activeIndex = 0;
        this.abortController = new AbortController();
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.addEventListener('click', (event) => this.handleClick(event), {
          signal: this.abortController.signal,
        });
        this.dialog?.addEventListener('close', () => this.cleanupDialog(), {
          signal: this.abortController.signal,
        });
        this.dialog?.addEventListener('keydown', (event) => this.handleKeydown(event), {
          signal: this.abortController.signal,
        });
        this.viewer?.addEventListener('scroll', () => this.handleViewerScroll(), {
          passive: true,
          signal: this.abortController.signal,
        });
        window.addEventListener('resize', () => this.handleResize(), {
          passive: true,
          signal: this.abortController.signal,
        });

        if (typeof ResizeObserver !== 'undefined' && this.rail) {
          this.resizeObserver = new ResizeObserver(() => this.updateRailControls());
          this.resizeObserver.observe(this.rail);
        }

        this.updateRailControls();
      }

      disconnectedCallback() {
        delete this.dataset.featuredVideoReady;
        this.abortController?.abort();
        this.resizeObserver?.disconnect();
        window.clearTimeout(this.scrollEndTimer);
        this.cleanupDialog();
      }

      handleClick(event) {
        const opener = event.target.closest('[data-featured-video-open]');
        if (opener && this.contains(opener)) {
          this.openDialog(Number(opener.dataset.videoIndex || 0), opener);
          return;
        }

        const closeButton = event.target.closest('[data-featured-video-close]');
        if (closeButton && this.contains(closeButton)) {
          this.closeDialog();
          return;
        }

        const railPrevious = event.target.closest('[data-featured-video-rail-previous]');
        if (railPrevious && this.contains(railPrevious)) {
          this.scrollRail(-1);
          return;
        }

        const railNext = event.target.closest('[data-featured-video-rail-next]');
        if (railNext && this.contains(railNext)) {
          this.scrollRail(1);
          return;
        }

        const previous = event.target.closest('[data-featured-video-previous]');
        if (previous && this.contains(previous)) {
          this.goToVideo(this.activeIndex - 1);
          return;
        }

        const next = event.target.closest('[data-featured-video-next]');
        if (next && this.contains(next)) this.goToVideo(this.activeIndex + 1);
      }

      handleKeydown(event) {
        if (event.target.closest('a, input, select, textarea, video')) return;

        if (event.key === 'ArrowUp' || event.key === 'PageUp') {
          event.preventDefault();
          this.goToVideo(this.activeIndex - 1);
        }

        if (event.key === 'ArrowDown' || event.key === 'PageDown') {
          event.preventDefault();
          this.goToVideo(this.activeIndex + 1);
        }
      }

      handleViewerScroll() {
        window.clearTimeout(this.scrollEndTimer);
        this.scrollEndTimer = window.setTimeout(() => {
          if (!this.viewer?.clientHeight) return;
          const index = Math.round(this.viewer.scrollTop / this.viewer.clientHeight);
          this.setActiveVideo(index);
        }, 90);
      }

      handleResize() {
        this.updateRailControls();
        if (!this.dialog?.open || !this.viewer) return;
        this.viewer.scrollTop = this.activeIndex * this.viewer.clientHeight;
      }

      scrollRail(direction) {
        if (!this.rail) return;
        const firstCard = this.rail.querySelector('[data-featured-video-item]');
        const cardWidth = firstCard?.getBoundingClientRect().width || this.rail.clientWidth * 0.75;
        const gap = Number.parseFloat(getComputedStyle(this.rail).columnGap) || 16;
        this.rail.scrollBy({ left: (cardWidth + gap) * direction, behavior: 'smooth' });
      }

      updateRailControls() {
        if (!this.rail || !this.railControls) return;
        this.railControls.hidden = this.rail.scrollWidth <= this.rail.clientWidth + 2;
      }

      openDialog(index, opener) {
        if (!this.dialog || !this.viewer || this.templates.length === 0) return;

        this.returnFocusTarget = opener;
        this.cleanupSlides();

        const slides = this.templates.map((template) => template.content.cloneNode(true));
        this.viewer.replaceChildren(...slides);
        this.slides = Array.from(this.viewer.querySelectorAll('[data-featured-video-slide]'));

        if (typeof this.dialog.showModal === 'function') {
          this.dialog.showModal();
        } else {
          this.dialog.setAttribute('open', '');
        }

        document.documentElement.classList.add('featured-video-shopping-open');
        this.activeIndex = Math.max(0, Math.min(index, this.slides.length - 1));

        requestAnimationFrame(() => {
          this.viewer.scrollTop = this.activeIndex * this.viewer.clientHeight;
          this.setActiveVideo(this.activeIndex, true);
          this.querySelector('[data-featured-video-close]')?.focus({ preventScroll: true });
        });
      }

      closeDialog() {
        if (!this.dialog) return;

        if (typeof this.dialog.close === 'function' && this.dialog.open) {
          this.dialog.close();
        } else {
          this.dialog.removeAttribute('open');
          this.cleanupDialog();
        }
      }

      goToVideo(index) {
        if (!this.viewer || !this.slides?.length) return;

        const nextIndex = Math.max(0, Math.min(index, this.slides.length - 1));
        if (nextIndex === this.activeIndex) return;

        this.activeIndex = nextIndex;
        this.viewer.scrollTo({
          top: nextIndex * this.viewer.clientHeight,
          behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
        });
        this.setActiveVideo(nextIndex);
      }

      setActiveVideo(index, forcePlay = false) {
        if (!this.slides?.length) return;

        this.activeIndex = Math.max(0, Math.min(index, this.slides.length - 1));
        this.slides.forEach((slide, slideIndex) => {
          const isActive = slideIndex === this.activeIndex;
          const video = slide.querySelector('video');

          slide.setAttribute('aria-hidden', String(!isActive));
          if (!video) return;

          if (isActive && (this.modalAutoplay || forcePlay)) {
            video.play().catch(() => {});
          } else if (!isActive) {
            video.pause();
          }
        });

        const previous = this.querySelector('[data-featured-video-previous]');
        const next = this.querySelector('[data-featured-video-next]');
        if (previous) previous.disabled = this.activeIndex === 0;
        if (next) next.disabled = this.activeIndex === this.slides.length - 1;
      }

      cleanupDialog() {
        document.documentElement.classList.remove('featured-video-shopping-open');
        this.cleanupSlides();
        this.viewer?.replaceChildren();
        this.slides = [];
        this.returnFocusTarget?.focus({ preventScroll: true });
        this.returnFocusTarget = null;
      }

      cleanupSlides() {
        this.viewer?.querySelectorAll('video').forEach((video) => {
          video.pause();
          video.removeAttribute('src');
          video.querySelectorAll('source').forEach((source) => source.removeAttribute('src'));
          video.load();
        });
      }
    }
  );
}
